
import Vue from 'vue';
import { Component, Watch } from 'vue-property-decorator';
import axios from 'axios';
import { getScannerEventService, ScannerEventService } from '@/services/ScannerEventService';
import { getHardwareService } from '@/services/HardwareIntegrationService';
import { getPrinterConfiguration } from '@/services/PrinterConfigurationService';
import { getShippingActivitySignalR, ShippingActivityEvent } from '@/services/ShippingActivitySignalRService';
import HardwareStatus from '@/components/Hardware/HardwareStatus.vue';
import SystemCategoryIcon from '@/components/SystemCategoryIcon.vue';
import { GET_SHIPPING_CONFIGURATION } from '@/gql/shipping';
import { GET_RECENT_SHIPPER_ACTIVITY } from '@/gql/shipper';

@Component({
  components: {
    HardwareStatus,
    SystemCategoryIcon,
  },
  filters: {
    formatDateTime(value: string) {
      if (!value) return '';
      const date = new Date(value);
      return date.toLocaleString();
    },
  },
})
export default class ShipperDashboard extends Vue {
  scannerActive = false;
  manualPickTicketId = '';
  pickTicketSearch = '';
  loadingPickTickets = false;
  loadingActivity = false;
  pendingPickTickets: any[] = [];
  recentActivity: any[] = [];
  scannerService: ScannerEventService | null = null;
  hardwareService: any = null;
  printerConfigService: any = null;
  configurationUnsubscribe: any = null;
  /* Printer configuration tracking
 */
  selectedAutoPrintPrinter: string | null = null;
  autoPrintEnabled = false;
  availablePrinters: any[] = [];
  hardwareCheckInterval: any = null;
  lastUhbState = false;
  testMode = false;

  // SignalR real-time updates
  signalRUnsubscribe: (() => void) | null = null;
  pendingActivityRefresh = false;
  showNewActivityIndicator = false;
  refreshDebounceTimer: number | null = null;

  pickTicketHeaders = [
    { text: 'Pick Ticket #', value: 'pickTicketNumber' },
    { text: 'Order #', value: 'orderNumber' },
    { text: 'Customer', value: 'customerName' },
    { text: 'Created', value: 'createdAt' },
    { text: 'Items', value: 'itemCount' },
    { text: 'Actions', value: 'actions', sortable: false, align: 'center' },
  ];

  async mounted() {
    /* Initialize hardware services first to ensure proper configuration loading
 */
    await this.initializeHardwareServices();
    await this.fetchShippingConfiguration();
    this.initializeScanner();
    this.loadPendingPickTickets();
    this.loadRecentActivity();

    /* Listen for global label creation events from GlobalShippingDialog */
    this.$bus.$on('shipping-label-created', this.onLabelCreated);

    /* Subscribe to real-time shipping activity updates via SignalR */
    this.signalRUnsubscribe = getShippingActivitySignalR().subscribe(this.onShippingActivityUpdate);

    /* Log initial configuration state for debugging
 */
    console.log('[ShipperDashboard] Initial state:', {
      uhbAvailable: this.uhbAvailable,
      selectedPrinter: this.selectedAutoPrintPrinter,
      autoPrintEnabled: this.autoPrintEnabled,
      isPrinterConfigurationValid: this.isPrinterConfigurationValid,
      testMode: this.testMode
    });
  }

  async fetchShippingConfiguration() {
    try {
      const { data } = await this.$apollo.query({
        query: GET_SHIPPING_CONFIGURATION,
        fetchPolicy: 'network-only'
      });

      if (data?.shippingConfiguration) {
        this.testMode = data.shippingConfiguration.testMode;
        console.log('[ShipperDashboard] Shipping configuration loaded:', { testMode: this.testMode });
      }
    } catch (error) {
      console.error('[ShipperDashboard] Failed to fetch shipping configuration:', error);
      // Default to false (live mode) on error
      this.testMode = false;
    }
  }

  beforeDestroy() {
    if (this.scannerService) {
      this.scannerService.removeListener('barcode-scanned', this.handleScan);
    }
    if (this.configurationUnsubscribe) {
      this.configurationUnsubscribe();
    }
    if (this.hardwareCheckInterval) {
      clearInterval(this.hardwareCheckInterval);
    }
    if (this.signalRUnsubscribe) {
      this.signalRUnsubscribe();
    }
    if (this.refreshDebounceTimer) {
      clearTimeout(this.refreshDebounceTimer);
    }
    /* Remove global event listener */
    this.$bus.$off('shipping-label-created', this.onLabelCreated);
  }

  initializeScanner() {
    this.scannerService = getScannerEventService();
    this.scannerService.addListener('barcode-scanned', this.handleScan);
    this.scannerActive = true;
  }

  async initializeHardwareServices() {
    try {
      /* Initialize hardware service (similar to HardwareStatus.vue)
 */
      this.hardwareService = getHardwareService();

      /* Wait a bit for hardware service to establish connection
 */
      await new Promise(resolve => setTimeout(resolve, 100));

      /* Initialize printer configuration service
 */
      this.printerConfigService = getPrinterConfiguration();

      /* Load current printer configuration
 */
      this.loadPrinterConfiguration();

      console.log('[ShipperDashboard] Loaded configuration:', {
        selectedPrinter: this.selectedAutoPrintPrinter,
        autoPrintEnabled: this.autoPrintEnabled,
        uhbConnected: this.hardwareService?.isConnected
      });

      /* Subscribe to configuration changes
 */
      this.configurationUnsubscribe = this.printerConfigService.onConfigurationChange((config: any) => {
        console.log('[ShipperDashboard] Printer configuration changed:', config);
        this.autoPrintEnabled = config.autoPrintLabels;
        this.selectedAutoPrintPrinter = config.labelPrinterId;
        /* Force Vue to re-evaluate computed properties
 */
        this.$forceUpdate();
      });

      /* Load available printers if UHB is connected
 */
      if (this.hardwareService?.isConnected) {
        await this.refreshPrinters();
      }

      /* Set up periodic check for UHB connection and configuration
 */
      this.hardwareCheckInterval = setInterval(() => {
        const wasConnected = this.lastUhbState;
        const isConnected = this.hardwareService?.isConnected || false;

        /* If UHB just connected, refresh printers
 */
        if (!wasConnected && isConnected) {
          console.log('[ShipperDashboard] UHB connected, refreshing printers');
          this.refreshPrinters();
        }

        this.lastUhbState = isConnected;
      }, 2000);
    } catch (error) {
      console.error('[ShipperDashboard] Failed to initialize hardware services:', error);
    }
  }

  loadPrinterConfiguration() {
    const config = this.printerConfigService.getConfiguration();
    this.autoPrintEnabled = config.autoPrintLabels;
    this.selectedAutoPrintPrinter = config.labelPrinterId;

    console.log('[ShipperDashboard] Configuration loaded from service:', {
      labelPrinterId: config.labelPrinterId,
      autoPrintLabels: config.autoPrintLabels
    });
  }

  async refreshPrinters() {
    try {
      if (!this.hardwareService?.isConnected) return;

      const printers = await this.hardwareService.discoverPrinters();
      this.availablePrinters = printers.map((printer: any) => ({
        id: printer.id,
        name: printer.name || printer.id,
        isConnected: printer.isConnected
      }));
    } catch (error) {
      console.error('[ShipperDashboard] Failed to refresh printers:', error);
    }
  }

  handleScan(event: any) {
    const raw = event.data || event;
    const barcode = typeof raw === 'string' ? raw.replace(/\*/g, '') : raw;
    console.log('Scanned barcode:', barcode);

    /* Extract pick ticket ID from barcode (assuming format like PT-12345)
 */
    const match = barcode.match(/PT-?(\d+)/i);
    if (match) {
      const pickTicketId = parseInt(match[1]);
      this.generateLabel(pickTicketId);
    } else if (/^\d+$/.test(barcode)) {
      /* If it's just a number, treat it as pick ticket ID
 */
      this.generateLabel(parseInt(barcode));
    }
  }

  processManualEntry() {
    if (this.manualPickTicketId) {
      const cleaned = this.manualPickTicketId.replace(/\*/g, '');
      const pickTicketId = parseInt(cleaned);
      if (!isNaN(pickTicketId)) {
        this.generateLabel(pickTicketId);
        this.manualPickTicketId = '';
      }
    }
  }

  async loadPendingPickTickets() {
    this.loadingPickTickets = true;
    try {
      const response = await axios.get('/api/shipper/pick-tickets');
      this.pendingPickTickets = response.data.map((pt: any) => ({
        ...pt,
        itemCount: pt.items?.length || 0,
      }));
    } catch (error) {
      console.error('Failed to load pending pick tickets:', error);
      this.$toast?.error('Failed to load pending pick tickets');
    } finally {
      this.loadingPickTickets = false;
    }
  }

  async loadRecentActivity() {
    this.loadingActivity = true;
    try {
      const { data } = await this.$apollo.query({
        query: GET_RECENT_SHIPPER_ACTIVITY,
        variables: { limit: 50 },
        fetchPolicy: 'network-only'
      });

      if (data?.recentShipperActivity) {
        this.recentActivity = data.recentShipperActivity;
      }
    } catch (error) {
      console.error('Failed to load recent activity:', error);
      this.$toast?.error('Failed to load recent activity');
    } finally {
      this.loadingActivity = false;
    }
  }

  selectPickTicket(item: any) {
    this.generateLabel(item.pickTicketId);
  }

  async generateLabel(pickTicketId: number) {
    try {
      console.log('[ShipperDashboard] Opening global shipping dialog for pickTicketId:', pickTicketId);

      // Use the global shipping dialog (same as scanning)
      await this.$store.dispatch('shippingDialog/openShippingDialog', pickTicketId);

      console.log('[ShipperDashboard] Global shipping dialog opened successfully');
    } catch (error: any) {
      console.error('[ShipperDashboard] Failed to open shipping dialog:', error);
      // Error already shown by the store action
    }
  }

  printPickTicket(item: any) {
    try {
      console.log('[ShipperDashboard] Opening print view for pick ticket:', item.pickTicketId);

      // Construct the shipper-specific print route URL
      const pickTicketId = item.pickTicketId;
      const printUrl = `/shipper/pick-ticket/${pickTicketId}/print`;

      // Open in new window for printing
      window.open(printUrl, '_blank');

      console.log('[ShipperDashboard] Print view opened successfully');
    } catch (error: any) {
      console.error('[ShipperDashboard] Failed to open print view:', error);
      this.$toast?.error('Failed to open print view');
    }
  }

  onLabelCreated(data: any) {
    // Listen for global label creation events
    console.log('[ShipperDashboard] Label created event:', data);

    // Refresh activities and pending pick tickets
    this.loadRecentActivity();
    this.loadPendingPickTickets();
  }

  /**
   * Handle SignalR shipping activity updates from other users.
   * Implements smart refresh to avoid interrupting active shipping operations.
   */
  onShippingActivityUpdate(event: ShippingActivityEvent) {
    console.log('[ShipperDashboard] SignalR shipping activity update:', event);

    // Check if user is currently busy with shipping dialog
    if (this.isShippingDialogOpen) {
      // User is busy - mark refresh as pending and show indicator
      console.log('[ShipperDashboard] Dialog open, deferring refresh');
      this.pendingActivityRefresh = true;
      this.showNewActivityIndicator = true;
      return;
    }

    // User is not busy - debounce the refresh to prevent rapid updates
    if (this.refreshDebounceTimer) {
      clearTimeout(this.refreshDebounceTimer);
    }

    this.refreshDebounceTimer = window.setTimeout(() => {
      console.log('[ShipperDashboard] Executing debounced refresh');
      this.loadRecentActivity();
      this.loadPendingPickTickets();
      this.showNewActivityIndicator = false;
      this.refreshDebounceTimer = null;
    }, 1000); // 1 second debounce
  }

  /**
   * Computed property to access shipping dialog state for watching.
   */
  get isShippingDialogOpen(): boolean {
    return this.$store.getters['shippingDialog/isDialogOpen'];
  }

  /**
   * Watch for shipping dialog close to process any pending refreshes.
   */
  @Watch('isShippingDialogOpen')
  onDialogStateChange(isOpen: boolean) {
    if (!isOpen && this.pendingActivityRefresh) {
      console.log('[ShipperDashboard] Dialog closed, processing pending refresh');
      this.pendingActivityRefresh = false;
      this.showNewActivityIndicator = false;
      this.loadRecentActivity();
      this.loadPendingPickTickets();
    }
  }

  formatDate(dateString: string): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    // Always display in Central Time
    return date.toLocaleDateString('en-US', { timeZone: 'America/Chicago' });
  }

  formatItemsHierarchy(items: any[]): any[] {
    if (!items || items.length === 0) return [];

    // Get all line item IDs in this pick ticket
    const itemIds = new Set(items.map(item => item.lineItemId));

    // Parent items are those whose parent is NOT in the pick ticket
    // (they are children of the sales order, but top-level in the pick ticket)
    const parentItems = items.filter(item =>
      !item.parentLineItemId || !itemIds.has(item.parentLineItemId)
    );

    // Child items are those whose parent IS in the pick ticket
    const childItems = items.filter(item =>
      item.parentLineItemId && itemIds.has(item.parentLineItemId)
    );

    // Build hierarchy
    return parentItems.map(parent => ({
      id: parent.lineItemId,
      styleCode: parent.styleCode,
      quantity: parent.quantity,
      systemCategoryId: parent.systemCategoryId,
      inventoryItemId: parent.inventoryItemId,
      children: childItems.filter(child => child.parentLineItemId === parent.lineItemId)
    }));
  }

  getActivityIcon(activity: any): string {
    switch (activity.activityType) {
      case 'LabelCreated':
        return activity.isReturnLabel ? 'mdi-label-variant' : 'mdi-label';
      case 'LabelVoided':
        return activity.isReturnLabel ? 'mdi-label-variant-outline' : 'mdi-label-off';
      case 'PickTicketShipped':
        return 'mdi-truck-check';
      default:
        return 'mdi-information';
    }
  }

  getActivityColor(activity: any): string {
    switch (activity.activityType) {
      case 'LabelCreated':
        return activity.isReturnLabel ? 'info' : 'success';
      case 'LabelVoided':
        return 'warning';
      case 'PickTicketShipped':
        return 'primary';
      default:
        return 'grey';
    }
  }

  getActivityLabel(activity: any): string {
    switch (activity.activityType) {
      case 'LabelCreated':
        return activity.isReturnLabel ? 'Return Label Created' : 'Label Created';
      case 'LabelVoided':
        return activity.isReturnLabel ? 'Return Label Voided' : 'Label Voided';
      case 'PickTicketShipped':
        return 'Ready to Ship';
      default:
        return activity.activityType;
    }
  }

  /**
   * Get the tracking URL for an activity, constructing it from carrier code and tracking number
   * if the stored URL doesn't match the tracking number (fixes multi-package shipment URLs)
   */
  getTrackingUrl(activity: any): string | null {
    if (!activity.trackingNumber) return null;

    // If stored URL contains this specific tracking number, use it
    if (activity.trackingUrl && activity.trackingUrl.includes(activity.trackingNumber)) {
      return activity.trackingUrl;
    }

    // Otherwise, construct URL from carrier code and tracking number
    if (activity.carrierCode) {
      const carrierLower = activity.carrierCode.toLowerCase();
      const encoded = encodeURIComponent(activity.trackingNumber);

      if (carrierLower.includes('ups')) {
        return `https://www.ups.com/track?tracknum=${encoded}`;
      } else if (carrierLower.includes('fedex')) {
        return `https://www.fedex.com/fedextrack/?trknbr=${encoded}`;
      } else if (carrierLower.includes('usps') || carrierLower.includes('stamps')) {
        return `https://tools.usps.com/go/TrackConfirmAction?tLabels=${encoded}`;
      } else if (carrierLower.includes('dhl')) {
        return `https://www.dhl.com/us-en/home/tracking/tracking-express.html?submit=1&tracking-id=${encoded}`;
      }
    }

    // Fallback to stored URL if we can't construct one
    return activity.trackingUrl || null;
  }

  formatRelativeTime(timestamp: string): string {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    return date.toLocaleDateString('en-US', { timeZone: 'America/Chicago' });
  }

  get canViewJumpLinks(): boolean {
    const user = this.$store.state.authentication?.user;
    if (!user) return false;
    return user.isAdmin || user.roles?.some((r: any) => r.name === 'shipper');
  }

  getOrderUrl(orderId: number): string {
    const resolved = this.$router.resolve({ name: 'Order', params: { orderId: String(orderId) } });
    return resolved.href;
  }

  getPickTicketPrintUrl(pickTicketId: number): string {
    const resolved = this.$router.resolve({ name: 'Shipper Pick Ticket Print', params: { pickTicketId: String(pickTicketId) } });
    return resolved.href;
  }

  /* Computed properties for printer configuration validation (similar to HardwareStatus.vue)
 */
  get uhbAvailable(): boolean {
    return this.hardwareService?.isConnected && !this.uhbError;
  }

  get uhbError(): any {
    return this.hardwareService?.lastError || null;
  }

  get isPrinterConfigurationValid(): boolean {
    /* All three conditions must be met for valid configuration
 */
    return this.uhbAvailable && this.hasSelectedPrinter && this.autoPrintEnabled;
  }

  get hasSelectedPrinter(): boolean {
    /* Check if a printer is selected in configuration
 */
    /* We trust the configuration - if a printer ID is set, we assume it's valid
 */
    /* The availablePrinters array might be empty if we haven't refreshed or if UHB just connected
 */
    return !!this.selectedAutoPrintPrinter;
  }

  get printerConfigurationMessage(): string {
    if (!this.uhbAvailable) {
      return 'Universal Hardware Bridge is not connected. Please start the UHB application to enable label printing.';
    }

    if (!this.selectedAutoPrintPrinter) {
      return 'No printer is selected for automatic label printing. Please select a printer from the available options.';
    }

    if (!this.autoPrintEnabled) {
      return 'Auto-print is disabled. Please enable automatic label printing to ensure labels print immediately after generation.';
    }

    return 'Printer configuration is incomplete. Please check your hardware setup.';
  }

  showHardwarePanel() {
    /* Open the printer settings dialog from the HardwareStatus component
 */
    const hardwareStatus = this.$refs.hardwareStatus as any;
    if (hardwareStatus) {
      console.log('[ShipperDashboard] Opening printer settings dialog');

      /* Ensure the component is properly initialized
 */
      if (!hardwareStatus.hardwareService) {
        hardwareStatus.initializeServices();
      }

      /* Load the latest printer settings
 */
      hardwareStatus.loadPrinterSettings();

      /* Refresh available printers if needed
 */
      if (hardwareStatus.availablePrinters.length === 0 && hardwareStatus.uhbAvailable) {
        hardwareStatus.refreshPrinters();
      }

      /* Show the printer settings dialog
 */
      hardwareStatus.showPrinterSettingsDialog = true;

      /* Subscribe to dialog close to reload our configuration
 */
      const checkDialogClosed = setInterval(() => {
        if (!hardwareStatus.showPrinterSettingsDialog) {
          clearInterval(checkDialogClosed);
          /* Reload our configuration after dialog closes
 */
          this.loadPrinterConfiguration();
          console.log('[ShipperDashboard] Printer settings dialog closed, reloaded configuration');
        }
      }, 500);
    } else {
      console.error('[ShipperDashboard] HardwareStatus component ref not found');
    }
  }
}
